import {
    secureDelete,
    secureGet,
    securePost,
    securePut,
} from "../../Config/ApiConfig";

/**
 * Get all credentials for the current user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.size - Page size
 * @param {string} params.search - Search string
 * @param {Object} params.filters - Filter object
 * @param {string} params.sort_by - Sort field
 * @param {string} params.sort_direction - Sort direction (asc/desc)
 * @returns {Promise} Response data
 */
export const getCredentials = async ({ page, size, search, filters, sort_by, sort_direction, signal }) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("page_size", size);

    if (search && search.trim()) {
        params.append("search_string", search.trim());
    }

    if (filters && Object.keys(filters).length > 0) {
        params.append("filters", JSON.stringify(filters));
    }

    if (sort_by) {
        params.append("sort_by", String(sort_by));
    }
    if (sort_direction) {
        params.append("sort_direction", String(sort_direction));
    }

    const { data } = await secureGet(`/credentials?${params.toString()}`, signal ? { signal } : {});
    return data;
};

/**
 * Create a new credential
 * @param {Object} body - Credential data
 * @param {string} body.channel_type - Channel type (facebook, instagram, etc.)
 * @param {string} body.short_lived_token - Short-lived access token
 * @returns {Promise} Response data
 */
export const createCredential = async (body) => {
    const { data } = await securePost('/credentials', body);
    return data;
};

/**
 * Update an existing credential
 * @param {string} id - Credential ID
 * @param {Object} body - Updated credential data
 * @param {string} body.short_lived_token - Short-lived access token
 * @param {string} body.name - Credential name (optional)
 * @returns {Promise} Response data
 */
export const updateCredential = async (id, body) => {
    const { data } = await securePut(`/credentials/${id}`, body);
    return data;
};

/**
 * Delete a credential
 * @param {string} id - Credential ID
 * @returns {Promise} Response data
 */
export const deleteCredential = async (id) => {
    const { data } = await secureDelete(`/credentials/${id}`);
    return data;
};

/**
 * Get resources (pages/accounts) available for a credential
 * @param {Object} params - Query parameters
 * @param {string} params.credential_id - Credential ID
 * @param {number} params.page - Page number
 * @param {number} params.size - Page size
 * @param {Object} params.filters - Filter object
 * @param {string} params.sort_by - Sort field
 * @param {string} params.sort_direction - Sort direction (asc/desc)
 * @returns {Promise} Response data
 */
export const getCredentialResources = async ({ credential_id, page, size, filters, sort_by, sort_direction }) => {
    const params = new URLSearchParams();
    params.append("page", page || 1);
    params.append("page_size", size || 10);

    if (filters && Object.keys(filters).length > 0) {
        params.append("filters", JSON.stringify(filters));
    }

    if (sort_by) {
        params.append("sort_by", String(sort_by));
    }
    if (sort_direction) {
        params.append("sort_direction", String(sort_direction));
    }

    const { data } = await secureGet(`/credentials/${credential_id}/resources?${params.toString()}`);
    return data;
};

